import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface RowActionsMenuProps {
  enabled: boolean;
  onToggleEnabled: () => void;
  /**
   * Optional — omit when the menu is rendered inside the edit view itself
   * (no point offering a "go to edit" item when you're already there).
   */
  onEdit?: () => void;
  onDelete: () => void;
}

/**
 * Compact `⋯` menu used in the reports list AND in the edit view header.
 * Closes on outside click, on Escape, and after any item is invoked.
 */
const RowActionsMenu = ({ enabled, onToggleEnabled, onEdit, onDelete }: RowActionsMenuProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const item = (label: string, onClick: () => void, className = "") => (
    <button
      type="button"
      onClick={() => {
        setOpen(false);
        onClick();
      }}
      className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${className}`}
    >
      {label}
    </button>
  );

  return (
    <div className="relative inline-block" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 rounded-lg text-gray-500 hover:bg-green-50 hover:text-green-700 transition-colors"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 5v.01M12 12v.01M12 19v.01"
          />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1"
        >
          {item(
            enabled
              ? t("pages.statistics.reports.actions.disable")
              : t("pages.statistics.reports.actions.enable"),
            onToggleEnabled,
            enabled ? "text-red-700 hover:bg-red-50" : "text-green-800 hover:bg-green-100",
          )}
          {onEdit && item(t("pages.statistics.reports.actions.edit"), onEdit)}
          <div className="border-t border-gray-100 my-1" />
          {item(
            t("pages.statistics.reports.actions.delete"),
            onDelete,
            "text-red-600 hover:bg-red-50",
          )}
        </div>
      )}
    </div>
  );
};

export default RowActionsMenu;
